/**
 * Dynamic Pricing Hook - Now uses global PricingContext
 * This prevents multiple API calls by sharing pricing data across all components
 * 
 * @version 2.0.0
 * @author TraderTekno Development Team
 */

import { useMemo } from 'react';
import { usePricingContext } from '../../contexts/PricingContext';
import type { TierConfiguration, PaymentMethodBonus, ActivePromotion } from '../../contexts/PricingContext';

// Legacy types for compatibility
export interface LegacyCulturalPricingInfo {
  isRamadan?: boolean;
  isEid?: boolean;
  isPaydayWeek?: boolean;
  isIndependenceMonth?: boolean;
  isYearEnd?: boolean;
  customEvent?: {
    name: string;
    discount: number;
    description?: string;
  };
}

// Re-export types from context
export type { TierConfiguration, PaymentMethodBonus, ActivePromotion };

/**
 * Dynamic Pricing Hook - Now uses global PricingContext
 * This prevents multiple API calls by sharing pricing data across all components
 */
export const useDynamicPricing = () => {
  const {
    tiers,
    culturalEvents,
    paymentBonuses,
    campaigns,
    loading,
    error,
    refreshPricingData,
    getActiveDiscounts: _getActiveDiscounts,
    getBestPaymentBonus: _getBestPaymentBonus
  } = usePricingContext();

  // Transform context data to match legacy interface for backward compatibility
  const culturalEventsLegacy: LegacyCulturalPricingInfo = useMemo(() => {
    const now = new Date();
    let legacyEvents: LegacyCulturalPricingInfo = {
      isRamadan: false,
      isEid: false,
      isPaydayWeek: false,
      isIndependenceMonth: false,
      isYearEnd: false
    };

    // Check if any cultural events are currently active
    culturalEvents.forEach(event => {
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      if (now >= startDate && now <= endDate) {
        const eventName = event.event.toLowerCase();
        if (eventName.includes('ramadan')) legacyEvents.isRamadan = true;
        if (eventName.includes('eid') || eventName.includes('lebaran')) legacyEvents.isEid = true;
        if (eventName.includes('payday') || eventName.includes('gajian')) legacyEvents.isPaydayWeek = true;
        if (eventName.includes('independence') || eventName.includes('kemerdekaan')) legacyEvents.isIndependenceMonth = true;
        if (eventName.includes('year') || eventName.includes('tahun')) legacyEvents.isYearEnd = true;
      }
    });

    return legacyEvents;
  }, [culturalEvents]);

  // Business logic functions
  const getDiscountedPrice = (tierIdOrPrice: string | number, tierName?: string) => {
    let basePrice: number;
    let currentTierName: string | undefined;
    
    // Handle both legacy (price only) and new (tierId) usage
    if (typeof tierIdOrPrice === 'string') {
      const tier = tiers.find(t => t.id === tierIdOrPrice);
      if (!tier) return { finalPrice: 0, originalPrice: 0, discount: 0, appliedDiscounts: [] };
      basePrice = tier.finalPrice ?? tier.basePrice ?? 0;
      currentTierName = tier.name;
    } else {
      basePrice = tierIdOrPrice;
      currentTierName = tierName;
    }
    
    let finalPrice = basePrice;
    const appliedDiscounts: string[] = [];
    let totalDiscount = 0;

    // Apply cultural discounts
    const culturalDiscount = calculateCulturalDiscount(basePrice, culturalEventsLegacy);
    if (culturalDiscount > 0) {
      finalPrice -= culturalDiscount;
      totalDiscount += culturalDiscount;
      
      if (culturalEventsLegacy.isRamadan) appliedDiscounts.push('Ramadan Special');
      if (culturalEventsLegacy.isEid) appliedDiscounts.push('Eid Mubarak');
      if (culturalEventsLegacy.isPaydayWeek) appliedDiscounts.push('Payday Special');
      if (culturalEventsLegacy.isIndependenceMonth) appliedDiscounts.push('Kemerdekaan');
      if (culturalEventsLegacy.isYearEnd) appliedDiscounts.push('Year-End Special');
    }

    // Apply promotional discounts from campaigns
    const now = new Date();
    const activePromotions = campaigns.filter(promo => {
      const startDate = new Date(promo.startDate);
      const endDate = new Date(promo.endDate);
      
      const isDateValid = now >= startDate && now <= endDate;
      const isTierValid = !promo.tiers || !currentTierName || promo.tiers.includes(currentTierName);
      
      return isDateValid && isTierValid;
    });

    activePromotions.forEach(promo => {
      if (promo.type === 'percentage') {
        const promoDiscount = basePrice * (promo.discount / 100);
        finalPrice -= promoDiscount;
        totalDiscount += promoDiscount;
      } else if (promo.type === 'fixed') {
        finalPrice -= promo.discount;
        totalDiscount += promo.discount;
      }
      appliedDiscounts.push(promo.name);
    });

    return {
      finalPrice: Math.max(0, Math.round(finalPrice)),
      originalPrice: basePrice,
      discount: Math.round(totalDiscount),
      appliedDiscounts
    };
  };

  const getPromotionalMessage = (): string | null => {
    const messages: string[] = [];

    // Cultural event messages
    if (culturalEventsLegacy.isRamadan) messages.push('🌙 Spesial Ramadan - Hemat hingga 15%!');
    if (culturalEventsLegacy.isEid) messages.push('🎉 Selamat Hari Raya - Diskon 20%!');
    if (culturalEventsLegacy.isPaydayWeek) messages.push('💰 Minggu Gajian - Cashback Extra!');
    if (culturalEventsLegacy.isIndependenceMonth) messages.push('🇮🇩 HUT RI - Promo Kemerdekaan!');
    if (culturalEventsLegacy.isYearEnd) messages.push('🎊 Tahun Baru - Resolusi Trading Sukses!');

    // Active promotion messages  
    const now = new Date();
    campaigns.forEach(promo => {
      const startDate = new Date(promo.startDate);
      const endDate = new Date(promo.endDate);
      
      if (now >= startDate && now <= endDate) {
        messages.push(`⚡ ${promo.name} - ${promo.discount}${promo.type === 'percentage' ? '%' : 'IDR'} OFF!`);
      }
    });

    return messages.length > 0 ? messages[0] : null;
  };

  const isSpecialEventActive = (): boolean => {
    return culturalEventsLegacy.isRamadan || 
           culturalEventsLegacy.isEid || 
           culturalEventsLegacy.isPaydayWeek || 
           culturalEventsLegacy.isIndependenceMonth ||
           culturalEventsLegacy.isYearEnd ||
           (culturalEventsLegacy.customEvent !== undefined) ||
           campaigns.length > 0;
  };

  const getPaymentMethodBonus = (method: string, amount: number): number => {
    const bonus = paymentBonuses[method.toLowerCase()];
    if (!bonus) return 0;
    return Math.round((amount * bonus) / 100);
  };

  const getTierByFeatures = (requiredFeatures: string[]): TierConfiguration | null => {
    return tiers.find(tier => 
      requiredFeatures.every(feature => 
        tier.features?.some(f => f.id === feature && f.included)
      )
    ) ?? null;
  };

  const refreshPricing = () => {
    refreshPricingData();
  };

  const invalidateCache = () => {
    refreshPricingData();
  };

  return {
    // Data
    tiers,
    activePromotions: campaigns,
    culturalEvents: culturalEventsLegacy,
    paymentMethodBonuses: paymentBonuses,
    
    // Status
    loading,
    error,
    lastUpdated: new Date(),
    
    // Utility functions
    getDiscountedPrice,
    getPromotionalMessage,
    getPaymentMethodBonus,
    getTierByFeatures,
    isSpecialEventActive,
    
    // Control functions
    refreshPricing,
    invalidateCache
  };
};

// Helper functions for backward compatibility
function calculateCulturalDiscount(price: number, culturalEvents: LegacyCulturalPricingInfo): number {
  let discount = 0;

  if (culturalEvents.isRamadan) discount += price * 0.15; // 15% Ramadan discount
  if (culturalEvents.isEid) discount += price * 0.20; // 20% Eid discount
  if (culturalEvents.isPaydayWeek) discount += price * 0.10; // 10% Payday discount
  if (culturalEvents.isIndependenceMonth) discount += price * 0.17; // 17% Independence discount (17 Aug)
  if (culturalEvents.isYearEnd) discount += price * 0.25; // 25% Year-end discount

  if (culturalEvents.customEvent) {
    discount += price * (culturalEvents.customEvent.discount / 100);
  }

  return Math.round(discount);
}